import { AppInsightsCore, IExtendedConfiguration, IExtendedTelemetryItem, ITelemetryItem } from "@microsoft/1ds-core-js";
import { PostChannel } from "@microsoft/1ds-post-js";
import { IPropertyConfiguration, PropertiesPlugin } from "@microsoft/1ds-properties-js";
import React from "react";
import { v4 } from "uuid";
import { EventContext } from "../../common/contexts/event";
import { useInterval } from "../../common/hooks/usetimeout";
import { getCookie } from "../../common/utilities/browser";
import { getFailureType } from "../../common/utilities/error";
import { ITelemetryEvent } from "../../common/utilities/platformdispatch";
import { ISessionContext, SessionContext } from "../contexts/session";
import { IFeatureContext } from "../../common/contexts/feature";
import { useSubscription } from "../../common/hooks/useobservable";

export const customerPromiseTable = "ev_customerpromise";
export const engagementTable = "ev_engagement";
export const experimentTable = "ev_experiment";
export const qosTable = "ev_qos";
export const unhandledErrorTable = "ev_unhandlederror";
export const privacyGuardEvent = "ev_privacyguard";

const telemetryTypePrefixMapping: { [key: string]: string } = {
  customerPromise: "CustomerPromise",
  engagement: "Engagement",
  exception: "Qos",
  fetchComplete: "Qos",
  fetchStart: "Qos",
  operationComplete: "Engagement",
  scenarioComplete: "Engagement",
  sessionComplete: "Engagement",
  userAction: "Engagement"
};

interface IFailedScenarioProperties {
  name: string;
  code: string;
  message: string;
}

export const batchWindowTime = 10000; // 10 seconds

const batchEventTypes = new Set(["downloadPhoto", "scrollWheelDown", "scrollWheelUp"]);
const emptyItems: ITelemetryItem[] = [];

function getItemsFromEvent(event: ITelemetryEvent, sessionContext: ISessionContext): ITelemetryItem[] {
  const eventType = event.action.toLowerCase();

  switch (eventType) {
    case "engagement": {
      const { action, scenarioName, name, ...extraData } = event;
      const page = sessionContext.getPage();
      const data = {
        Name: `PhotosEngagement.${scenarioName || name}`,
        CurrentPage: page.current,
        ...prepareExtraData(action, extraData, sessionContext)
      };

      return [{ name: engagementTable, data }];
    }

    case "exception": {
      const { action, colno, error, filename, lineno, name, scenarioName, stack, ...extraData } = event;

      if (name === "unhandledException") {
        const data = {
          Name: `OneDrivePhotos.unhandledException`,
          UnhandledError_col: colno,
          UnhandledError_filename: filename,
          UnhandledError_line: lineno,
          UnhandledError_message: error,
          UnhandledError_stack: stack,
          ...prepareExtraData(action, extraData, sessionContext)
        };

        return [{ name: unhandledErrorTable, data }];
      } else {
        const ResultCode = error?.code || error || "noErrorCode";
        const Error = error?.message || error;
        const data = {
          Name: `OneDrivePhotos.${scenarioName || name}`,
          WebLog_EventType: "End",
          Error,
          ResultCode,
          ResultType: getFailureType(ResultCode, Error),
          ...prepareExtraData(action, extraData, sessionContext)
        };

        return [{ name: qosTable, data }];
      }
    }

    case "fetchcomplete": {
      const { action, duration, ms_cv: CorrelationVector, name, result, scenarioName, url: Api_url, ...extraData } = event;

      // Convert the result into the proper set of reported properties.
      const resultStatus = {
        ResultType: "Success"
      };

      if (result?.status === "rejected") {
        const ResultCode = result.reason?.error?.code || result?.reason?.message || "noResultCode";
        const Error = result.reason?.error?.message || result?.reason?.message;
        const responseStatus = result.reason?.statusCode || result.reason?.error?.statusCode;

        Object.assign(resultStatus, {
          Error,
          ResultCode,
          ResultType: getFailureType(ResultCode, Error),
          responseStatus
        });
      }

      const data = {
        Name: `OneDrivePhotos.${scenarioName || name}`,
        Api_url,
        CorrelationVector,
        WebLog_EventType: "End",
        Duration: duration?.toString(),
        ...resultStatus,
        ...prepareExtraData(action, extraData, sessionContext)
      };

      return [{ name: qosTable, data }];
    }

    case "fetchstart": {
      const { action, options, name, scenarioName, url: Api_url, ...extraData } = event;

      const data = {
        Name: `OneDrivePhotos.${scenarioName || name}`,
        Api_url,
        WebLog_EventType: "Start",
        ...prepareExtraData(action, extraData, sessionContext)
      };

      return [{ name: qosTable, data }];
    }

    case "operationcomplete":
    case "scenariocomplete": {
      const { action, children, duration, properties, scenarioName, name } = event;

      const page = sessionContext.getPage();

      let failedSubScenarios: IFailedScenarioProperties[] = populateFailedSubSenarios(event, []);

      const data = {
        Name: `PhotosScenario.${scenarioName || name}`,
        CurrentPage: page.current,
        PreviousPage: page.previous,
        ...prepareExtraData(
          action,
          {
            ...properties,
            duration,
            ResultType: failedSubScenarios.length > 0 ? "Failure" : "Success",
            error: failedSubScenarios.length > 0 ? JSON.stringify(failedSubScenarios[0]) : undefined,
            subScenario: scenarioName === "photosAppLoad" ? children && children.length && children[0].scenarioName : undefined
          },
          sessionContext
        )
      };

      let items: ITelemetryItem[] = [{ name: engagementTable, data }];

      // If the scenarie/operation is tracked for ASHA, send a customer promise telemetry
      if (properties?.pillar) {
        const data = {
          AccountType: sessionContext.authenticated() ? (sessionContext.migrated ? "ConsumerOnSPO" : "Consumer") : "ConsumerAnonymous",
          ClientName: "ODCPhotos Web",
          CustomerPromise_extraData_excludeTDC: properties.excludeTDC,
          CustomerPromise_extraData_Pillar: properties.pillar,
          CustomerRing: sessionContext.ring,
          Duration: duration?.toString(),
          PerfGoalInMs: properties.perfGoalInMs,
          ResultType: "Success",
          Scenario: scenarioName,
          WebLog_EventType: "End"
        };

        if (failedSubScenarios.length > 0) {
          // TODO: We only want to send one failed customer promise, temporarily promote the first failed subScenario
          const { name: firstFailedSenarioName, ...error } = failedSubScenarios[0];

          Object.assign(data, {
            ErrorCode: error.code,
            CustomerPromise_extraData_veto: firstFailedSenarioName + "Failed", //TODO: Need to have the correct veto name for each error
            CustomerPromise_extraData_errormessage: error.message,
            ResultType: getFailureType(error.code, error.message)
          });
        } else if (duration && properties.perfGoalInMs && duration > properties.perfGoalInMs) {
          // TODO: Temporarily only handles the perfGoal for the parent scenario. Need more work.
          Object.assign(data, {
            ErrorCode: "Missing perf goal",
            ResultType: "Failure",
            CustomerPromise_extraData_veto: "MissingPerfGoal" //TODO: Need to have the correct veto name for each error
          });
        }
        items.push({ name: customerPromiseTable, data });
      }

      return items;

      function populateFailedSubSenarios(event: ITelemetryEvent, failed: IFailedScenarioProperties[]): IFailedScenarioProperties[] {
        const { children, result, scenarioName } = event;

        if (result?.status === "rejected" && !result.reason?.isCanceled) {
          failed.push({
            name: scenarioName,
            code: result.reason?.error?.code || result.reason?.message || "noErrorCode",
            message: result.reason?.error?.message || result.reason?.message || JSON.stringify(result.reason)
          });
        }

        for (let child of children || []) {
          populateFailedSubSenarios(child, failed);
        }

        return failed;
      }
    }

    case "sessioncomplete": {
      const { action, name, ...extraData } = event;
      const data = {
        Name: `Session.${name}`,
        ...prepareExtraData(action, extraData, sessionContext)
      };

      return [{ name: engagementTable, data }];
    }

    case "useraction": {
      const { action, scenarioName, name, ...extraData } = event;
      const page = sessionContext.getPage();
      const data = {
        Name: `PhotosAction.${scenarioName || name}`,
        CurrentPage: page.current,
        PreviousPage: page.previous,
        ...prepareExtraData(action, extraData, sessionContext)
      };

      return [{ name: engagementTable, data }];
    }

    case "experimentevaluation": {
      const { name, variant, defaultValue = "Default" } = event;

      const page = sessionContext.getPage();
      const data = {
        Experiment: name,
        Variant: variant,
        IsInRamp: variant !== defaultValue,
        ClientName: "ODCPhotos Web",
        CurrentPage: page.current
      };

      return [{ name: experimentTable, data }];
    }

    default:
      return emptyItems;
  }
}

function prepareExtraData(action: string, data: { [property: string]: any }, sessionContext: ISessionContext): any {
  const extraData: { [property: string]: any } = {
    Standalone: sessionContext.standalone || false,
    ...data
  };
  const payloadExtraData: { [key: string]: any } = {};

  for (let key in extraData) {
    const value = extraData[key];
    payloadExtraData[`${telemetryTypePrefixMapping[action]}_extraData_${key}`] = typeof value === "object" ? JSON.stringify(value) : value;
  }

  return payloadExtraData;
}

export function useOneDS(featureContext: IFeatureContext): ((event: ITelemetryEvent) => void) | void {
  const eventContext = React.useContext(EventContext);
  const sessionContext = React.useContext(SessionContext);

  const [oneDsLogger] = React.useState(() => new AppInsightsCore());
  const [instanceId] = React.useState(v4());

  const batchedEvents = React.useRef<{ [key: string]: ITelemetryEvent }>({});
  const pgIsEnabled = useSubscription(featureContext.featureEnabled("EnablePrivacyGuard"));

  const { setInterval } = useInterval();

  const sendItems = React.useCallback(
    (logItems: IExtendedTelemetryItem[] | ITelemetryItem[]) => {
      // istanbul ignore next - DEVELOPMENT: Show OneDS telemetry logs in the console.
      if (process.env.NODE_ENV !== "test" && (sessionContext.environment === "development" || getCookie("Development"))) {
        if (logItems.length) {
          for (let index = 0; index < logItems.length; index++) {
            console.debug(logItems[index].name, logItems[index]);
          }
        }
      }

      for (let logItem of logItems) {
        oneDsLogger.track(logItem);
      }
    },
    [oneDsLogger, sessionContext]
  );

  const sendBatchedItems = React.useCallback(() => {
    const items = Object.keys(batchedEvents.current).reduce((accumulator: IExtendedTelemetryItem[] | ITelemetryItem[], action: string) => {
      const item = batchedEvents.current[action];
      const { totalDuration, ...eventData } = item;

      // Set the average duration for this batched item
      eventData.duration = Math.ceil(totalDuration / item.count);

      // Add the items generated from this event to the resulting items batch
      accumulator.push(...getItemsFromEvent(eventData, sessionContext));
      return accumulator;
    }, []);

    // clean all entries
    batchedEvents.current = {};

    sendItems(items);
  }, [sendItems, sessionContext]);

  setInterval(sendBatchedItems, batchWindowTime);

  // Ensure an ariaToken has been defined, if not telemetry events
  if (sessionContext.ariaToken) {
    try {
      // Based on the users current authentication and migration state record the details.
      const authenticated = sessionContext.authenticated();
      const environment = `ODCPhotos_${sessionContext.ring === "dogfood" ? "DF" : sessionContext.environment === "production" ? "Prod" : "Dev"}`;

      // Setup the properties plugin with all the static fields for each event.
      const propertiesPlugin = new PropertiesPlugin();
      propertiesPlugin.setProperty("AccountType", authenticated ? (sessionContext.migrated ? "ConsumerOnSPO" : "Consumer") : "ConsumerAnonymous");
      propertiesPlugin.setProperty("AppInfo.Manifest", "photos.manifest"); // The manifest comes from coreConfig.ts on the server.
      propertiesPlugin.setProperty("AppInfo.Version", sessionContext.clientVersion);
      propertiesPlugin.setProperty("BrowserIsMobile", !!navigator.userAgent.toLowerCase().match(/mobile/i));
      propertiesPlugin.setProperty("BrowserUserAgent", navigator.userAgent);
      propertiesPlugin.setProperty("Environment", environment);
      propertiesPlugin.setProperty("IsAuthenticated", authenticated && sessionContext.userProfile ? "1" : "0");
      propertiesPlugin.setProperty("Workload", "ODCPhotos");
      propertiesPlugin.setProperty("InstanceId", instanceId);

      if (sessionContext.session) {
        propertiesPlugin.setProperty("SessionId", sessionContext.session);
      }

      const propertiesPluginConfig: IPropertyConfiguration = {
        populateBrowserInfo: true
      };

      const coreConfig: IExtendedConfiguration = {
        channels: [[new PostChannel()]],
        cookieCfg: { enabled: false },
        endpointUrl: sessionContext.telemetryEndpoint,
        instrumentationKey: sessionContext.ariaToken,
        extensions: [propertiesPlugin],
        extensionConfig: {
          PrivacyGuardPlugin: {
            notificationEventName: privacyGuardEvent,
            iKey: sessionContext.ariaToken,
            enabled: pgIsEnabled,
            disableAdvancedScans: true,
            scanForUrls: false
          },
          [propertiesPlugin.identifier]: propertiesPluginConfig
        }
      };

      // Initialize the oneDS SDK
      oneDsLogger.initialize(coreConfig, []);

      if (authenticated && sessionContext.userId) {
        // istanbul ignore else - Con't figure out how to remove BigInt from window.
        const userId = "BigInt" in window ? BigInt.asIntN(64, BigInt(`0x${sessionContext.userId}`)).toString(10) : sessionContext.userId;
        propertiesPlugin.setProperty("UserInfo_Id", userId);
        propertiesPlugin.getPropertiesContext().user.localId = `m:${userId}`;
      }

      // Return the event reporting function to the caller
      return (event: ITelemetryEvent): void => {
        if (batchEventTypes.has(event.name)) {
          // Add the current event to the batch
          const { action, name, result, duration, error } = event;
          const itemKey = `${action}-${name}-${result?.status || ""}-${error?.code || error?.message || ""}`;
          if (batchedEvents.current[itemKey]) {
            batchedEvents.current[itemKey].count++;
            batchedEvents.current[itemKey].totalDuration += duration;
          } else {
            batchedEvents.current[itemKey] = {
              ...event,
              count: 1,
              totalDuration: duration
            };
          }
        } else {
          sendItems(getItemsFromEvent(event, sessionContext));
        }
      };
    } catch (error) {
      eventContext.dispatchEvent("telemetryAvailable", { action: "exception", name: "1dsInitialization", error });
    }
  }
}
